import React, {useState, useEffect, useRef} from 'react';
import translateCopyPrompt from './translateCopyPrompt.ts';
import {
    Button,
    Collapse,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Col,
    Alert,
    Spinner,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import {MixModel, ProjectDataIndex} from '../../api/types.ts';
import Icon from '../shared/Icon.tsx';
import {
    BrainIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    DicesIcon,
    Gamepad2Icon,
    InfoIcon,
    LightbulbIcon,
    Settings2Icon,
    SquarePenIcon
} from 'lucide-react';
import {ScrewdriverWrenchIcon} from '../shared/customIcons.ts';
import useVibeCode from '../../api/assistant/useVibeCode.ts';
import {useMutation} from 'react-query';
import {toast} from 'react-toastify';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import VibeCodingOutput from './VibeCodingOutput.tsx';
import templatePromptsData, {
    appCategories,
    gameCategories,
    TemplateCategory
} from './templatePromptsData.ts';
import getRandomItem from '../../lib/getRandomItem.ts';
import {
    buildPromptFromDetails,
    initialWizardState,
    randomDataSources,
    TemplateKind,
    WizardDataState,
    WizardField
} from './vibe.ts';

// Builder field labels/placeholders adapt to whether the user is building a
// game or a practical app/utility.
const fieldConfig: Record<
    TemplateKind,
    Record<WizardField, {label: string; placeholder: string}>
> = {
    game: {
        gameName: {
            label: 'Game Name',
            placeholder: 'e.g., Space Invaders, Maze Runner'
        },
        objective: {
            label: 'Objective',
            placeholder: 'What the player needs to do to win or progress?'
        },
        coreMechanics: {
            label: 'Core Mechanics',
            placeholder:
                'How does the player interact? e.g., Arrow key movement, mouse click to shoot.'
        },
        visualStyle: {
            label: 'Visual Style',
            placeholder: 'e.g., Retro arcade, Minimalist, Cartoon'
        },
        keyElements: {
            label: 'Key Elements to Include',
            placeholder:
                'Important UI components and features. e.g., Score display, lives counter.'
        }
    },
    app: {
        gameName: {
            label: 'App Name',
            placeholder: 'e.g., Focus Timer, Shopping List'
        },
        objective: {
            label: 'What It Does',
            placeholder: 'What problem does this tool solve for the user?'
        },
        coreMechanics: {
            label: 'How It Works',
            placeholder:
                'How does the user interact? e.g., Add items, check them off, edit inline.'
        },
        visualStyle: {
            label: 'Visual Style',
            placeholder: 'e.g., Clean minimal, Dark mode, Colorful cards'
        },
        keyElements: {
            label: 'Key Features to Include',
            placeholder:
                'Features and data to save. e.g., Saves to localStorage, categories, totals.'
        }
    }
};
import WizardFieldComponent from './WizardFieldComponent.tsx';

interface AssistantVibeCodingProps {
    mix: MixModel;
    dataIndex: ProjectDataIndex;
    mode: 'vibe' | 'edit';
    existingHtml?: string;
    // On a just-remixed game: lead with "What would you like to change?".
    askWhatToChange?: boolean;
    // A copy made from the translator: start translating it into this
    // language straight away, and save when the AI has finished.
    translateInto?: string | null;
    onApplyHtml: (html: string) => void;
    onClose: () => void;
    onDiscard?: () => void;
}

const AssistantVibeCoding: React.FC<AssistantVibeCodingProps> = ({
    mix,
    dataIndex,
    mode,
    existingHtml,
    askWhatToChange = false,
    translateInto = null,
    onApplyHtml,
    onClose,
    onDiscard
}) => {
    const [prompt, setPrompt] = useState<string>(() =>
        translateInto ? translateCopyPrompt(translateInto) : ''
    );
    const [generationFinished, setGenerationFinished] = useState(false);
    const [wizardData, setWizardData] =
        useState<WizardDataState>(initialWizardState);

    const [isVibeCoding, setIsVibeCoding] = useState<boolean>(false);
    // The finished HTML reported by the output once generation completes.
    // Null while still generating, which keeps "Save and close" disabled.
    const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
    // A problem reported by the output (stall, dropped connection, empty
    // result). When set, we stop showing the "Generating…" reassurance.
    const [generationError, setGenerationError] = useState<string | null>(null);
    const [activeKind, setActiveKind] = useState<TemplateKind>('game');
    const [activeCategory, setActiveCategory] = useState<
        TemplateCategory | 'all'
    >('all');
    const [tipsOpen, setTipsOpen] = useState<boolean>(mode === 'edit');
    const [selectedTemplate, setSelectedTemplate] = useState<string | null>(
        null
    );

    const vibeCode = useVibeCode();
    const vibeCodeMutation = useMutation(vibeCode, {
        onSuccess: () => {
            setIsVibeCoding(true);
        },
        onError: (error: unknown) => {
            toast.error('Error vibe coding: ' + unknownErrorToString(error));
        }
    });

    // Initialize prompt based on mode
    useEffect(() => {
        if (mode === 'edit' && existingHtml && !translateInto) {
            setPrompt(''); // Start with empty prompt for user to describe changes
        }
    }, [mode, existingHtml, translateInto]);

    const handleTemplateSelect = (
        templateDetails: WizardDataState,
        templateTitle: string
    ) => {
        setWizardData(templateDetails);
        setSelectedTemplate(templateTitle);
        const templatePrompt = buildPromptFromDetails(
            templateDetails,
            activeKind
        );
        setPrompt(templatePrompt);
        toast.info(
            `Template "${
                templateDetails.gameName || 'selected template'
            }" loaded.`
        );
    };

    const handleWizardInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const {name, value} = e.target;
        const newWizardData = {...wizardData, [name as WizardField]: value};
        setWizardData(newWizardData);

        // Auto-update prompt as user types
        const updatedPrompt = buildPromptFromDetails(newWizardData, activeKind);
        setPrompt(updatedPrompt);
    };

    const handleWizardSuggestionClick = (name: WizardField, value: string) => {
        setWizardData(prev => {
            let newData;
            if (name === 'keyElements') {
                const currentValue = prev.keyElements.trim();
                // Prevent adding duplicates
                const existingElements = currentValue
                    .toLowerCase()
                    .split(/,\s*|\s+and\s+/);
                if (existingElements.includes(value.toLowerCase())) {
                    toast.info(`"${value}" is already in Key Elements.`);
                    return prev;
                }
                if (currentValue === '') {
                    newData = {...prev, [name]: value};
                } else {
                    newData = {...prev, [name]: `${currentValue}, ${value}`};
                }
            } else {
                newData = {...prev, [name]: value};
            }

            // Update prompt with new data
            const updatedPrompt = buildPromptFromDetails(newData, activeKind);
            setPrompt(updatedPrompt);

            return newData;
        });
    };

    const generateRandomIdeaForWizard = () => {
        const pool = randomDataSources[activeKind];
        const newWizardData: WizardDataState = {
            gameName: getRandomItem(pool.gameName),
            objective: getRandomItem(pool.objective),
            coreMechanics: getRandomItem(pool.coreMechanics),
            visualStyle: getRandomItem(pool.visualStyle),
            keyElements: [
                getRandomItem(pool.keyElements),
                getRandomItem(
                    pool.keyElements.filter(el => el !== wizardData.keyElements)
                ) // try to get a different second one
            ]
                .filter(Boolean)
                .join(', ') // Ensure no undefined if array is short
        };
        setWizardData(newWizardData);
        setSelectedTemplate(null);
        const updatedPrompt = buildPromptFromDetails(newWizardData, activeKind);
        setPrompt(updatedPrompt);
        toast.success('Idea generated');
    };

    // Fires (or re-fires) generation from the current prompt. Shared by the
    // form submit and the "Try again" retry so both reset state identically.
    const startGeneration = () => {
        setGeneratedHtml(null);
        setGenerationError(null);
        setGenerationFinished(false);

        let finalPrompt = prompt;

        // If in edit mode, append the existing HTML code
        if (mode === 'edit' && existingHtml) {
            // Edit mode: Start with base instructions, add user's edit request, then append existing code
            finalPrompt = `Modify the existing single-page HTML app below as requested.

Edit request: ${prompt}

Output a complete, self-contained HTML file with CSS and JavaScript that incorporates all the requested modifications. Make sure to include comments explaining how the code works and what changes were made.

Here is the current HTML code to modify:

${existingHtml}`;
        }

        vibeCodeMutation.mutateAsync({
            projectId: mix.id!,
            prompt: finalPrompt,
            // The journal keeps what the person asked for, not the long
            // instructions behind a translation.
            historyPrompt: translateInto
                ? `Translate into ${translateInto}`
                : prompt,
            dataIndex,
            isEdit: mode === 'edit'
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!prompt.trim()) {
            toast.warn('Prompt cannot be empty.');
            return;
        }

        startGeneration();
    };

    const getDescriptionText = () => {
        return mode === 'edit'
            ? 'Describe the changes you want to make to your existing HTML code. The AI will modify your current code based on your instructions.'
            : 'Create an HTML game or app! Use the builder to get started, or write your prompt directly in the editor.';
    };

    const getPromptPlaceholder = () => {
        if (askWhatToChange) {
            return 'e.g. make it faster, change the colours, add a score';
        }
        return mode === 'edit'
            ? 'Describe what changes you want to make to the existing code...'
            : 'Describe your idea in detail...';
    };

    const getButtonText = () => {
        if (vibeCodeMutation.isLoading) {
            return mode === 'edit' ? 'Updating...' : 'Generating...';
        }
        return mode === 'edit' ? 'Update Code!' : 'Generate!';
    };

    const getButtonIcon = () => {
        return mode === 'edit' ? SquarePenIcon : Gamepad2Icon;
    };

    const saveAndClose = () => {
        if (!generatedHtml) {
            return;
        }
        setIsVibeCoding(false);
        onApplyHtml(generatedHtml);
        onClose();
    };

    // Translating a copy needs nobody to press anything: it starts once the
    // code is here, and saves once the AI has finished.
    // Started a tick after mounting: React's development double mount drops a
    // react-query mutation begun during the first one, which would leave it
    // "in progress" forever.
    const autoStarted = useRef(false);
    useEffect(() => {
        if (!translateInto || !existingHtml || autoStarted.current) {
            return;
        }
        const timer = window.setTimeout(() => {
            autoStarted.current = true;
            startGeneration();
        });
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translateInto, existingHtml]);

    useEffect(() => {
        if (translateInto && generationFinished && generatedHtml) {
            toast.success(`Translated into ${translateInto}`);
            saveAndClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translateInto, generationFinished, generatedHtml]);

    const discardAndClose = () => {
        setIsVibeCoding(false);
        (onDiscard ?? onClose)();
    };

    const handleKindChange = (kind: TemplateKind) => {
        setActiveKind(kind);
        setActiveCategory('all');
    };

    const categoriesForKind =
        activeKind === 'game' ? gameCategories : appCategories;
    const kindTemplates = templatePromptsData.filter(
        t => t.kind === activeKind
    );
    const visibleTemplates =
        activeCategory === 'all'
            ? kindTemplates
            : kindTemplates.filter(t => t.category === activeCategory);
    const builderFields = fieldConfig[activeKind];

    return (
        <>
            <ModalBody>
                <div className="assistant-vibe-coding">
                    {/* Show vibe coding progress alert when processing */}
                    {vibeCodeMutation.isLoading && (
                        <Alert color="warning" className="mb-4">
                            <div className="d-flex align-items-center">
                                <Icon
                                    icon={BrainIcon}
                                    className="me-3"
                                    size="lg"
                                />
                                <div>
                                    <h6 className="mb-1">
                                        <strong>
                                            Vibe Coding in Progress...
                                        </strong>
                                    </h6>
                                    <p className="mb-0 small">
                                        Please bear with us! Our AI is using a
                                        sophisticated thinking and reasoning
                                        model to craft your{' '}
                                        {mode === 'edit' ? 'updated' : 'new'}{' '}
                                        code. This process may take a bit longer
                                        than usual, but the results will be
                                        worth the wait.
                                    </p>
                                </div>
                                <Spinner
                                    type="grow"
                                    size="sm"
                                    color="warning"
                                    className="ms-3"
                                />
                            </div>
                        </Alert>
                    )}

                    {translateInto && (
                        <div className="mb-4">
                            <h4>Translating into {translateInto}…</h4>
                            <p className="text-muted">
                                This is your own copy. The AI is changing its
                                words into {translateInto}, and it saves by
                                itself when it&rsquo;s done. This can take a
                                minute or two, so keep this page open.
                            </p>
                        </div>
                    )}

                    {/* Show normal description when not vibe coding */}
                    {!vibeCodeMutation.isLoading &&
                        !translateInto &&
                        askWhatToChange && (
                            <div className="mb-4">
                                <h4>What would you like to change?</h4>
                                <p className="text-muted">
                                    This is your own copy now. Say what you want
                                    in your own words, like &ldquo;make the
                                    buttons bigger&rdquo; or &ldquo;add a
                                    timer&rdquo;, and the AI changes it for you.
                                </p>
                            </div>
                        )}
                    {!vibeCodeMutation.isLoading &&
                        !translateInto &&
                        !askWhatToChange && (
                            <div className="mb-4">
                                <p className="text-muted">
                                    {getDescriptionText()}
                                </p>
                                {mode === 'edit' && existingHtml && (
                                    <Alert color="info" className="small">
                                        <Icon
                                            icon={InfoIcon}
                                            className="me-2"
                                        />
                                        You are editing existing code. Describe
                                        your changes in the prompt, and the AI
                                        will modify your current HTML
                                        accordingly.
                                    </Alert>
                                )}
                            </div>
                        )}

                    {isVibeCoding && !vibeCodeMutation.isLoading && (
                        <>
                            {!generationError && (
                                <p className="small text-muted">
                                    {generatedHtml
                                        ? 'Your code is ready. Choose “Save and close” to run it in the live preview and fine-tune it in the code editor.'
                                        : 'Generating… you can save once the code is complete.'}
                                </p>
                            )}

                            <VibeCodingOutput
                                id={mix.id!}
                                onHtmlReady={setGeneratedHtml}
                                onError={setGenerationError}
                                onRetry={startGeneration}
                                onComplete={() => setGenerationFinished(true)}
                            />
                        </>
                    )}

                    {!isVibeCoding && !vibeCodeMutation.isLoading && (
                        <Row className="g-3">
                            {/* Only show builder in vibe mode */}
                            {mode === 'vibe' && (
                                <Col md="7" lg="8">
                                    <div className="vibe-panel">
                                        <span className="vibe-step">
                                            Step 1 · Build
                                        </span>
                                        <h4>
                                            <Icon
                                                icon={Settings2Icon}
                                                className="me-2"
                                            />
                                            Builder
                                        </h4>

                                        <h5 className="mt-4">Templates</h5>
                                        <p className="small text-muted">
                                            Choose Games or Apps &amp; Tools,
                                            pick a category, then click a
                                            template to pre-fill the builder
                                            fields and prompt.
                                        </p>

                                        <div
                                            className="vibe-kind-toggle"
                                            role="tablist"
                                            aria-label="Template type"
                                        >
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    activeKind === 'game'
                                                }
                                                className={`vibe-kind-toggle__btn${
                                                    activeKind === 'game'
                                                        ? ' is-active'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleKindChange('game')
                                                }
                                            >
                                                <Icon
                                                    icon={Gamepad2Icon}
                                                    className="me-2"
                                                />
                                                Games
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={
                                                    activeKind === 'app'
                                                }
                                                className={`vibe-kind-toggle__btn${
                                                    activeKind === 'app'
                                                        ? ' is-active'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    handleKindChange('app')
                                                }
                                            >
                                                <Icon
                                                    icon={ScrewdriverWrenchIcon}
                                                    className="me-2"
                                                />
                                                Apps &amp; Tools
                                            </button>
                                        </div>

                                        <div className="vibe-filter-bar">
                                            <button
                                                type="button"
                                                className={`vibe-filter-pill${
                                                    activeCategory === 'all'
                                                        ? ' is-active'
                                                        : ''
                                                }`}
                                                aria-pressed={
                                                    activeCategory === 'all'
                                                }
                                                onClick={() =>
                                                    setActiveCategory('all')
                                                }
                                            >
                                                All
                                                <span className="vibe-filter-pill__count">
                                                    {kindTemplates.length}
                                                </span>
                                            </button>
                                            {categoriesForKind.map(cat => {
                                                const count =
                                                    kindTemplates.filter(
                                                        t => t.category === cat
                                                    ).length;
                                                return (
                                                    <button
                                                        key={cat}
                                                        type="button"
                                                        className={`vibe-filter-pill${
                                                            activeCategory ===
                                                            cat
                                                                ? ' is-active'
                                                                : ''
                                                        }`}
                                                        aria-pressed={
                                                            activeCategory ===
                                                            cat
                                                        }
                                                        onClick={() =>
                                                            setActiveCategory(
                                                                cat
                                                            )
                                                        }
                                                    >
                                                        {cat}
                                                        <span className="vibe-filter-pill__count">
                                                            {count}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <div className="vibe-template-grid mb-3">
                                            {visibleTemplates.map(template => (
                                                <button
                                                    type="button"
                                                    key={template.title}
                                                    className={`vibe-template-card${
                                                        selectedTemplate ===
                                                        template.title
                                                            ? ' is-selected'
                                                            : ''
                                                    }`}
                                                    onClick={() =>
                                                        handleTemplateSelect(
                                                            template.details,
                                                            template.title
                                                        )
                                                    }
                                                >
                                                    <span className="vibe-template-card__icon">
                                                        <Icon
                                                            icon={template.icon}
                                                        />
                                                    </span>
                                                    <span className="vibe-template-card__title">
                                                        {template.title}
                                                    </span>
                                                    <span className="vibe-template-card__desc">
                                                        {template.description}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>

                                        <hr />

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h5 className="mb-0">
                                                Builder Fields
                                            </h5>
                                            <Button
                                                color="warning"
                                                size="sm"
                                                outline
                                                onClick={
                                                    generateRandomIdeaForWizard
                                                }
                                            >
                                                <Icon
                                                    icon={DicesIcon}
                                                    className="me-2"
                                                />
                                                Randomise!
                                            </Button>
                                        </div>

                                        <Form>
                                            <WizardFieldComponent
                                                label={
                                                    builderFields.gameName.label
                                                }
                                                name="gameName"
                                                kind={activeKind}
                                                value={wizardData.gameName}
                                                placeholder={
                                                    builderFields.gameName
                                                        .placeholder
                                                }
                                                onChange={
                                                    handleWizardInputChange
                                                }
                                                onSuggestionClick={
                                                    handleWizardSuggestionClick
                                                }
                                            />
                                            <WizardFieldComponent
                                                label={
                                                    builderFields.objective
                                                        .label
                                                }
                                                name="objective"
                                                kind={activeKind}
                                                value={wizardData.objective}
                                                placeholder={
                                                    builderFields.objective
                                                        .placeholder
                                                }
                                                onChange={
                                                    handleWizardInputChange
                                                }
                                                onSuggestionClick={
                                                    handleWizardSuggestionClick
                                                }
                                                isTextarea
                                                rows={3}
                                            />
                                            <WizardFieldComponent
                                                label={
                                                    builderFields.coreMechanics
                                                        .label
                                                }
                                                name="coreMechanics"
                                                kind={activeKind}
                                                value={wizardData.coreMechanics}
                                                placeholder={
                                                    builderFields.coreMechanics
                                                        .placeholder
                                                }
                                                onChange={
                                                    handleWizardInputChange
                                                }
                                                onSuggestionClick={
                                                    handleWizardSuggestionClick
                                                }
                                                isTextarea
                                                rows={4}
                                            />
                                            <WizardFieldComponent
                                                label={
                                                    builderFields.visualStyle
                                                        .label
                                                }
                                                name="visualStyle"
                                                kind={activeKind}
                                                value={wizardData.visualStyle}
                                                placeholder={
                                                    builderFields.visualStyle
                                                        .placeholder
                                                }
                                                onChange={
                                                    handleWizardInputChange
                                                }
                                                onSuggestionClick={
                                                    handleWizardSuggestionClick
                                                }
                                            />
                                            <WizardFieldComponent
                                                label={
                                                    builderFields.keyElements
                                                        .label
                                                }
                                                name="keyElements"
                                                kind={activeKind}
                                                value={wizardData.keyElements}
                                                placeholder={
                                                    builderFields.keyElements
                                                        .placeholder
                                                }
                                                onChange={
                                                    handleWizardInputChange
                                                }
                                                onSuggestionClick={
                                                    handleWizardSuggestionClick
                                                }
                                                isTextarea
                                                rows={4}
                                            />
                                        </Form>
                                    </div>
                                </Col>
                            )}

                            <Col
                                md={mode === 'edit' ? '12' : '5'}
                                lg={mode === 'edit' ? '12' : '4'}
                            >
                                <div
                                    className="vibe-panel"
                                    style={{position: 'sticky', top: '20px'}}
                                >
                                    {mode === 'vibe' && (
                                        <span className="vibe-step">
                                            Step 2 · Generate
                                        </span>
                                    )}
                                    {/* The question above already says it on a remixed game. */}
                                    {!askWhatToChange && (
                                        <>
                                            <h4>
                                                {mode === 'edit'
                                                    ? 'Edit Instructions'
                                                    : 'Prompt Editor'}
                                            </h4>
                                            <p className="small text-muted">
                                                {mode === 'edit'
                                                    ? 'Describe what changes you want to make to your existing HTML code.'
                                                    : 'Review and edit your description. The prompt updates automatically as you use the builder, or you can edit it directly.'}
                                            </p>
                                        </>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        <FormGroup>
                                            <Label
                                                for="codePrompt"
                                                className="sr-only"
                                            >
                                                {askWhatToChange
                                                    ? 'What to change:'
                                                    : mode === 'edit'
                                                    ? 'Your Edit Instructions:'
                                                    : 'Your Description:'}
                                            </Label>
                                            <Input
                                                type="textarea"
                                                id="codePrompt"
                                                className="vibe-prompt-input"
                                                value={prompt}
                                                onChange={e =>
                                                    setPrompt(e.target.value)
                                                }
                                                placeholder={getPromptPlaceholder()}
                                                rows={mode === 'edit' ? 10 : 16}
                                                disabled={
                                                    vibeCodeMutation.isLoading
                                                }
                                            />
                                        </FormGroup>

                                        <Button
                                            type="submit"
                                            className="w-100 vibe-generate-btn"
                                            disabled={
                                                !prompt.trim() ||
                                                vibeCodeMutation.isLoading
                                            }
                                            size="lg"
                                        >
                                            <Icon
                                                icon={getButtonIcon()}
                                                className="me-2"
                                            />
                                            {getButtonText()}
                                        </Button>
                                    </Form>

                                    <div className="vibe-help">
                                        <button
                                            type="button"
                                            className="vibe-help__toggle"
                                            aria-expanded={tipsOpen}
                                            onClick={() => setTipsOpen(o => !o)}
                                        >
                                            <Icon
                                                icon={LightbulbIcon}
                                                className="me-2"
                                            />
                                            {mode === 'edit'
                                                ? 'Editing tips & debugging'
                                                : 'Tips'}
                                            <Icon
                                                icon={
                                                    tipsOpen
                                                        ? ChevronUpIcon
                                                        : ChevronDownIcon
                                                }
                                                className="ms-auto"
                                            />
                                        </button>
                                        <Collapse isOpen={tipsOpen}>
                                            <div className="vibe-help__body">
                                                {mode === 'edit' ? (
                                                    <>
                                                        <p>
                                                            Describe the
                                                            specific changes you
                                                            want to make. The AI
                                                            will modify your
                                                            existing code based
                                                            on your
                                                            instructions.
                                                        </p>
                                                        <ul>
                                                            <li>
                                                                Be specific
                                                                about what you
                                                                want to change
                                                                or fix.
                                                            </li>
                                                            <li>
                                                                Describe any
                                                                bugs or issues
                                                                you're
                                                                experiencing.
                                                            </li>
                                                            <li>
                                                                Mention colors,
                                                                sizes,
                                                                positions, or
                                                                behaviors you
                                                                want to modify.
                                                            </li>
                                                            <li>
                                                                You can ask to
                                                                add new features
                                                                or remove
                                                                existing ones.
                                                            </li>
                                                        </ul>
                                                        <p className="vibe-help__subheading">
                                                            Debugging help
                                                        </p>
                                                        <ul>
                                                            <li>
                                                                <strong>
                                                                    Not working?
                                                                </strong>{' '}
                                                                Describe what
                                                                should happen
                                                                vs. what
                                                                actually
                                                                happens.
                                                            </li>
                                                            <li>
                                                                <strong>
                                                                    JavaScript
                                                                    errors?
                                                                </strong>{' '}
                                                                Open Developer
                                                                Tools (F12 or
                                                                Ctrl+Shift+I) →
                                                                Console tab.
                                                            </li>
                                                            <li>
                                                                <strong>
                                                                    Copy error
                                                                    messages:
                                                                </strong>{' '}
                                                                Right-click
                                                                error in console
                                                                → "Copy
                                                                message".
                                                            </li>
                                                            <li>
                                                                <strong>
                                                                    Visual
                                                                    issues?
                                                                </strong>{' '}
                                                                Describe what
                                                                looks wrong
                                                                (positioning,
                                                                colors, sizing).
                                                            </li>
                                                        </ul>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p>
                                                            Use the builder
                                                            fields on the left
                                                            to create your game
                                                            or app idea. The
                                                            prompt updates
                                                            automatically, or
                                                            you can edit it
                                                            directly for more
                                                            control.
                                                        </p>
                                                        <ul>
                                                            <li>
                                                                Study the
                                                                generated code
                                                                to understand
                                                                how it works.
                                                            </li>
                                                            <li>
                                                                Modify values in
                                                                the code to see
                                                                their effect.
                                                            </li>
                                                            <li>
                                                                You can further
                                                                edit the code in
                                                                the project
                                                                editor after
                                                                applying it.
                                                            </li>
                                                        </ul>
                                                    </>
                                                )}
                                            </div>
                                        </Collapse>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                {isVibeCoding ? (
                    <>
                        <Button
                            color="link"
                            className="me-auto text-decoration-none"
                            onClick={() => discardAndClose()}
                        >
                            Discard and close
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => saveAndClose()}
                            disabled={!generatedHtml}
                            title={
                                generatedHtml
                                    ? undefined
                                    : 'Available once the generated HTML is complete'
                            }
                        >
                            <Icon icon={SquarePenIcon} className="me-2" />
                            Save and close
                        </Button>
                    </>
                ) : (
                    <Button color="secondary" onClick={onClose}>
                        Close
                    </Button>
                )}
            </ModalFooter>
        </>
    );
};

export default AssistantVibeCoding;
